import React from 'react';
import { CardContent, Typography, Box, Stack, Switch, FormControlLabel } from '@mui/material';
import {
  MoreVert,
  Schedule,
  Notifications,
  NotificationsActive,
  AccessTime,
  CalendarToday,
  Phone,
  AlternateEmail,
  Delete,
} from '@mui/icons-material';
import { ActionMenu, ActionMenuItem } from './ActionMenu.styles';
import { ActiveStateAvatar, ActiveStateCard, ActiveStateChip } from './ActiveState.styles';
import { HoverScaleIconButton } from './HoverScaleIconButton.styles';
import { IconTextRow } from './IconTextRow';
import { LineClampTypography } from 'src/UIKit';
import { MicroLabel } from './MicroLabel.styles';
import { Alert } from '../../types';
import {
  getModeDescription,
  getProcessMessagesDescription,
  getPeriodDescription,
  getTriggerDescription,
  getDaysOfWeekText,
} from '../../helpers/alertsHelpers';

interface AlertCardProps {
  alert: Alert;
  companyName?: string;
  onEdit?: (alert: Alert) => void;
  onDelete?: (alertId: string) => void;
  onToggleActive?: (alert: Alert) => void;
}

export const AlertCard: React.FC<AlertCardProps> = ({
  alert,
  companyName,
  onEdit,
  onDelete,
  onToggleActive,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click when editing from menu
    onEdit?.(alert);
    handleMenuClose();
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click when deleting
    onDelete?.(alert.id);
    handleMenuClose();
  };

  const handleToggleActive = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent card click when toggling
    onToggleActive?.(alert);
  };

  const handleCardClick = () => {
    onEdit?.(alert);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation(); // Prevent card click when opening menu
    setAnchorEl(event.currentTarget);
  };

  const getDeliveryMethods = () => {
    const methods = [];
    if (alert.destinationEmail) {
      methods.push({ type: 'email', value: alert.destinationEmail, icon: AlternateEmail });
    }
    if (alert.destinationPhoneNumber) {
      methods.push({ type: 'phone', value: alert.destinationPhoneNumber, icon: Phone });
    }
    return methods;
  };

  const deliveryMethods = getDeliveryMethods();
  const isActive = alert.isActive;

  return (
    <ActiveStateCard active={isActive} onClick={handleCardClick} sx={{ height: 270 }}>
      <CardContent sx={{ p: 2, height: '100%' }}>
        <Stack spacing={1.5} height="100%">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <ActiveStateAvatar active={isActive}>
                {isActive ? (
                  <NotificationsActive fontSize="small" />
                ) : (
                  <Notifications fontSize="small" />
                )}
              </ActiveStateAvatar>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(event, checked) => {
                      console.log(
                        `AlertCard - Switch onChange triggered for alert ID: ${alert.id}`,
                        {
                          switchChecked: checked,
                          alertIsActive: alert.isActive,
                          currentIsActive: isActive,
                        },
                      );
                      handleToggleActive(event);
                    }}
                    size="small"
                    color="success"
                  />
                }
                label={
                  <Typography
                    variant="caption"
                    fontSize="0.7rem"
                    fontWeight="fontWeightSemiBold"
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </Typography>
                }
                onClick={e => e.stopPropagation()} // Prevent card click when clicking label
              />
            </Stack>

            <HoverScaleIconButton onClick={handleMenuClick} size="small">
              <MoreVert fontSize="small" />
            </HoverScaleIconButton>
          </Stack>

          <Box>
            <Typography
              variant="subtitle2"
              fontWeight="fontWeightSemiBold"
              color="text.primary"
              fontSize="0.85rem"
              lineHeight={1.2}
              mb={0.5}
            >
              {getModeDescription(alert.mode)}
            </Typography>
            <LineClampTypography
              variant="body2"
              color="text.secondary"
              fontSize="0.7rem"
              lineHeight={1.3}
              lines={2}
            >
              {getProcessMessagesDescription(alert.processMessages)}
            </LineClampTypography>
          </Box>

          <Stack spacing={0.5}>
            {companyName && (
              <Typography
                variant="caption"
                fontWeight="fontWeightMedium"
                color="text.primary"
                lineHeight={1.2}
                mb={0.5}
              >
                {companyName}
              </Typography>
            )}
            <IconTextRow icon={Schedule}>
              Every {alert.interval} {getPeriodDescription(alert.period).toLowerCase()}
            </IconTextRow>
            <IconTextRow icon={Notifications}>
              {getTriggerDescription(alert.onlyIfUnreadMessages)}
            </IconTextRow>
            <IconTextRow icon={AccessTime}>
              {alert.period === 'day' ? alert.startTime : `${alert.startTime} - ${alert.endTime}`}
            </IconTextRow>
            <IconTextRow icon={CalendarToday} multiline>
              {getDaysOfWeekText(alert.daysOfWeek)}
            </IconTextRow>
          </Stack>

          <Box>
            <MicroLabel variant="caption" mb={0.5}>
              Delivery
            </MicroLabel>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {deliveryMethods.map((method, index) => (
                <ActiveStateChip
                  key={index}
                  active={isActive}
                  icon={React.createElement(method.icon, { fontSize: 'small' })}
                  label={method.type === 'email' ? 'Email' : 'SMS'}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        </Stack>

        <ActionMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <ActionMenuItem onClick={handleEdit}>
            <Schedule fontSize="small" />
            Edit Alert
          </ActionMenuItem>
          <ActionMenuItem destructive onClick={handleDelete}>
            <Delete fontSize="small" />
            Delete Alert
          </ActionMenuItem>
        </ActionMenu>
      </CardContent>
    </ActiveStateCard>
  );
};
